import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatAxiosError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Cpu, Loader2, Check, Globe, BarChart3 } from "lucide-react";

const PROVIDERS = [["emergent", "Défaut"], ["openai", "OpenAI"], ["anthropic", "Anthropic"], ["gemini", "Gemini"]];
const PLACEHOLDER = { openai: "gpt-4o", anthropic: "claude-sonnet-4-6", gemini: "gemini-2-flash", emergent: "gpt-4o (défaut)" };

const fmtEur = (n) => `${(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 4 })} €`;
const fmtPct = (n) => `${Math.round((n || 0) * 100)} %`;
const fmtInt = (n) => (n || 0).toLocaleString("fr-FR");

export default function AdminAI() {
  const [form, setForm] = useState({ llm_provider: "emergent", llm_model: "", llm_api_key: "" });
  const [keySet, setKeySet] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState(null);
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(true);

  const load = () => api.get("/admin/llm").then((r) => {
    setForm({ llm_provider: r.data.llm_provider || "emergent", llm_model: r.data.llm_model || "", llm_api_key: "" });
    setKeySet(!!r.data.llm_key_set);
  });
  const loadUsage = () => {
    setUsageLoading(true);
    api.get("/admin/ai-usage", { params: { days: 30 } })
      .then((r) => setUsage(r.data))
      .catch(() => setUsage(null))
      .finally(() => setUsageLoading(false));
  };
  useEffect(() => { load(); loadUsage(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { llm_provider: form.llm_provider, llm_model: form.llm_model || "" };
      if (form.llm_provider !== "emergent" && form.llm_api_key) payload.llm_api_key = form.llm_api_key;
      await api.put("/admin/llm", payload);
      await load();
      setForm((f) => ({ ...f, llm_api_key: "" }));
      toast.success("Moteur IA global enregistré");
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setSaving(false); }
  };

  const runTest = async () => {
    setTesting(true); setTest(null);
    try {
      const payload = { llm_provider: form.llm_provider, llm_model: form.llm_model || "" };
      if (form.llm_provider !== "emergent" && form.llm_api_key) payload.llm_api_key = form.llm_api_key;
      const { data } = await api.post("/admin/llm-test", payload);
      setTest(data);
      data.ok ? toast.success(`Connexion IA OK (${data.provider} · ${data.latency_ms} ms)`) : toast.error("Échec de la connexion IA");
    } catch (e) { setTest({ ok: false, error: formatAxiosError(e) }); toast.error("Échec du test IA"); }
    finally { setTesting(false); }
  };

  return (
    <div className="max-w-3xl space-y-8" data-testid="admin-ai-page">
      <div>
        <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">Moteur IA global</h1>
        <p className="text-muted-foreground mt-2">Configuration du fournisseur d'IA appliquée à <strong>toute la plateforme</strong>. Une société peut surcharger ce réglage avec sa propre clé dans ses Paramètres.</p>
      </div>

      <div className="bg-card border border-border rounded-sm p-6 lg:p-8 space-y-5" data-testid="admin-llm-settings">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-primary" />
            <h3 className="font-heading text-lg font-semibold">Fournisseur par défaut</h3>
          </div>
          <span className={`text-[10px] tracking-wide uppercase px-2 py-1 rounded-sm font-semibold ${form.llm_provider !== "emergent" && keySet ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-muted text-muted-foreground"}`} data-testid="admin-llm-status">
            {form.llm_provider === "emergent" ? "Défaut" : (keySet ? "Clé propre" : "Clé requise")}
          </span>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Fournisseur</Label>
            <div className="mt-1.5 grid grid-cols-2 gap-2" data-testid="admin-llm-provider">
              {PROVIDERS.map(([v, lbl]) => (
                <button key={v} type="button" onClick={() => setForm((f) => ({ ...f, llm_provider: v }))}
                  data-testid={`admin-llm-provider-${v}`}
                  className={`h-10 text-sm font-medium rounded-sm border transition-colors ${form.llm_provider === v ? "bg-primary text-white border-primary" : "bg-transparent text-muted-foreground border-border hover:bg-muted"}`}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label>Modèle</Label>
            <Input value={form.llm_model} onChange={(e) => setForm((f) => ({ ...f, llm_model: e.target.value }))}
              data-testid="admin-llm-model" placeholder={PLACEHOLDER[form.llm_provider]} className="mt-1.5 rounded-sm" />
            <p className="text-xs text-muted-foreground mt-1.5">Laissez vide pour le modèle par défaut du fournisseur.</p>
          </div>
        </div>

        {form.llm_provider !== "emergent" && (
          <div>
            <Label>Clé API {form.llm_provider}</Label>
            <Input type="password" value={form.llm_api_key} data-testid="admin-llm-key"
              onChange={(e) => setForm((f) => ({ ...f, llm_api_key: e.target.value }))}
              placeholder={keySet ? "•••••••• (inchangée)" : "Votre clé API"} className="mt-1.5 rounded-sm" />
            <p className="text-xs text-muted-foreground mt-1.5">Stockée de façon sécurisée, jamais renvoyée. En cas d'échec, le fournisseur par défaut prend le relais.</p>
          </div>
        )}

        <div className="pt-1 flex items-center gap-3 flex-wrap">
          <Button onClick={save} disabled={saving} data-testid="admin-save-llm" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
            {saving ? "Enregistrement…" : "Enregistrer le moteur IA"}
          </Button>
          <Button onClick={runTest} disabled={testing} variant="outline" data-testid="admin-test-llm" className="rounded-sm font-semibold">
            {testing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Test en cours…</> : "Tester la connexion IA"}
          </Button>
        </div>
        {test && (
          <div data-testid="admin-llm-test-result" className={`text-sm rounded-sm px-3 py-2.5 border ${test.ok ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-red-50 text-red-800 border-red-200"}`}>
            {test.ok ? (
              <span><Check className="w-4 h-4 inline mr-1.5 -mt-0.5" />Connexion réussie — <strong>{test.provider}</strong> · {test.model} · {test.latency_ms} ms. Réponse : « {test.sample} »</span>
            ) : (<span><strong>Échec :</strong> {test.error}</span>)}
          </div>
        )}
      </div>

      <div className="bg-card border border-border rounded-sm p-6 lg:p-8 space-y-5" data-testid="admin-ai-usage">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-primary" />
          <h3 className="font-heading text-lg font-semibold">Consommation IA (30 j)</h3>
        </div>

        {usageLoading ? (
          <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Chargement…</p>
        ) : !usage || usage.total.calls === 0 ? (
          <p className="text-sm text-muted-foreground">Aucun appel IA journalisé sur la période.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-ai-usage-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border">
                  <th className="py-2 pr-4 font-medium">Société</th>
                  <th className="py-2 pr-4 font-medium text-right">Appels</th>
                  <th className="py-2 pr-4 font-medium text-right">Tokens (in/out)</th>
                  <th className="py-2 pr-4 font-medium text-right">Coût estimé</th>
                  <th className="py-2 pr-4 font-medium text-right">Latence moy.</th>
                  <th className="py-2 font-medium text-right">Succès</th>
                </tr>
              </thead>
              <tbody>
                {usage.companies.map((row) => (
                  <tr key={row.company_slug} className="border-b border-border/60">
                    <td className="py-2 pr-4 font-medium">
                      {row.company_slug === "_platform" ? "Plateforme (hors société)" : row.company_slug}
                    </td>
                    <td className="py-2 pr-4 text-right">{fmtInt(row.calls)}</td>
                    <td className="py-2 pr-4 text-right text-muted-foreground">
                      {fmtInt(row.prompt_tokens)} / {fmtInt(row.completion_tokens)}
                    </td>
                    <td className="py-2 pr-4 text-right">{fmtEur(row.est_cost_eur)}</td>
                    <td className="py-2 pr-4 text-right text-muted-foreground">{fmtInt(row.avg_latency_ms)} ms</td>
                    <td className="py-2 text-right">
                      <span className={row.ok_rate >= 0.95 ? "text-emerald-700" : row.ok_rate >= 0.8 ? "text-amber-700" : "text-red-700"}>
                        {fmtPct(row.ok_rate)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-semibold">
                  <td className="py-2 pr-4">Total</td>
                  <td className="py-2 pr-4 text-right">{fmtInt(usage.total.calls)}</td>
                  <td className="py-2 pr-4 text-right text-muted-foreground">
                    {fmtInt(usage.total.prompt_tokens)} / {fmtInt(usage.total.completion_tokens)}
                  </td>
                  <td className="py-2 pr-4 text-right">{fmtEur(usage.total.est_cost_eur)}</td>
                  <td className="py-2 pr-4 text-right text-muted-foreground">{fmtInt(usage.total.avg_latency_ms)} ms</td>
                  <td className="py-2 text-right">{fmtPct(usage.total.ok_rate)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
        <p className="text-xs text-muted-foreground">Coût estimé à titre indicatif (tarifs par modèle approximatifs) — ne remplace pas la facture réelle du fournisseur.</p>
      </div>
    </div>
  );
}
